import { useEffect, useMemo, useState } from "react";
import { Shield } from "lucide-react";
import { n2, shortDate } from "@/lib/format";
import { BestWorst, DateFilter, ExportButton, exportCsv, summarize, teamLogo as logo } from "./AccuracyTools";

const f1 = (v) => Number(v).toFixed(1);
const errTone = (e) => (Math.abs(e) <= 6 ? "good" : Math.abs(e) <= 12 ? "warn" : "bad");

function Metrics({ s, games, src, day }) {
  const cells = [["MAE PTS / TEAM", n2(s.mae_pts)], ["MAE REB", n2(s.mae_reb)], ["MAE AST", n2(s.mae_ast)], ["MAE 3PM", n2(s.mae_fg3m)],
    ["MAE GAME TOTAL", s.mae_game_total == null ? "–" : n2(s.mae_game_total)],
    ["WINNER RIGHT", s.winner_pct == null ? "–" : `${Math.round(s.winner_pct * 100)}%`, s.winner_pct == null ? "" : s.winner_pct >= 0.5 ? "positive" : "negative"],
    ["BIAS PTS", `${s.bias_pts > 0 ? "+" : ""}${f1(s.bias_pts)}`, "accent"]];
  return (
    <section className="aa-metrics" aria-label="Team total accuracy metrics" data-testid={`tt-summary-${src}`}>
      <div className="aa-sample">{src === "pregame" ? "SNAPSHOT" : "BACKTEST"} · {s.teams} team games · {games} games{day !== "all" ? ` · ${shortDate(day)}` : ""}</div>
      <div className="aa-metrics-grid">
        {cells.map(([k, v, tone]) => <div className="aa-metric" key={k}><span>{k}</span><strong className={tone || ""}>{v}</strong></div>)}
      </div>
    </section>
  );
}

function Team({ t }) {
  const e = t.error.pts;
  return (
    <span className="aa-team">
      <span className="aa-logo">
        <img src={logo(t.team_code)} alt="" loading="lazy" onError={(ev) => { ev.currentTarget.style.display = "none"; ev.currentTarget.nextSibling.style.display = "grid"; }} />
        <Shield className="aa-logo-fallback" size={20} aria-hidden="true" />
      </span>
      <strong className="aa-code">{t.team_code}</strong>
      <span className="aa-number">{f1(t.proj.pts)}</span><span className="aa-vs">vs</span><span className="aa-number">{t.actual.pts}</span>
      <span className={`aa-delta ${errTone(e)}`}>({e > 0 ? "+" : ""}{f1(e)})</span>
    </span>
  );
}

export function TeamTotalsAccuracy({ data, mode }) {
  const src = mode === "saved" ? "pregame" : "backtest";
  const [day, setDay] = useState("all");
  const all = useMemo(() => (data?.games || []).filter((g) => g.source === src), [data, src]);
  const days = useMemo(() => [...new Set(all.map((g) => g.game_day))].sort().reverse(), [all]);
  useEffect(() => { if (day !== "all" && !days.includes(day)) setDay("all"); }, [days, day]);
  if (data === false) return null;
  const games = day === "all" ? all : all.filter((g) => g.game_day === day);
  const s = day === "all" ? data?.summary?.[src] : summarize(games);
  return (
    <section className="aa-section" data-testid="team-totals-accuracy">
      <h2>Team totals vs real scores</h2>
      <p className="aa-explainer">
        Rotation-adjusted team totals ({data?.season_label}). <b>Pregame</b> = last snapshot saved before tip-off.{" "}
        <b>Backtest</b> = rebuilt from data before tip-off on the players who actually played (<b className="aa-warn">optimistic</b>: knows who was available, not a true pregame result).
        {data?.skipped_games ? ` ${data.skipped_games} games skipped (no current-season rotation yet, e.g. opening night).` : ""}
      </p>
      {!data && <p className="aa-empty">Calculating…</p>}
      {data && all.length > 0 && (
        <div className="aa-toolbar">
          <DateFilter days={days} value={day} onChange={setDay} />
          <ExportButton onClick={() => exportCsv(games, src, day)} disabled={!games.length} />
        </div>
      )}
      {data && s && <Metrics s={s} games={games.length} src={src} day={day} />}
      {data && games.length > 0 && <BestWorst games={games} />}
      {data && !s && <p className="aa-empty" data-testid="tt-no-snapshots">{src === "pregame" ? "No finished game with a pregame snapshot yet: today's games appear here after they finish." : "No backtested games yet."}</p>}
      {data && games.length > 0 && (
        <div className="aa-table-scroll">
          <table className="aa-table" data-testid="tt-games-table">
            <thead><tr><th>Date</th><th>Away proj vs real</th><th>Home proj vs real</th><th className="r">Game total</th><th className="c">Winner</th><th>Source</th></tr></thead>
            <tbody>
              {games.map((g) => (
                <tr key={g.game_id} data-testid={`tt-game-${g.game_id}`}>
                  <td className="aa-date">{shortDate(g.game_day)}</td>
                  <td><Team t={g.away} /></td>
                  <td><Team t={g.home} /></td>
                  <td className="r aa-total"><strong>{f1(g.proj_total)}</strong> <span className="aa-vs">vs</span> <strong>{g.actual_total}</strong></td>
                  <td className={`c ${g.winner_correct ? "aa-correct" : "aa-wrong"}`} aria-label={g.winner_correct ? "Winner correct" : "Winner wrong"}>{g.winner_correct ? "✓" : "✕"}</td>
                  <td><span className={`aa-source ${src}`}>{src === "pregame" ? "SNAPSHOT" : "BACKTEST"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
